import { ActionTree } from 'vuex'
import { eventHub } from '@/store/eventHub'
import { ModuleState, Notice } from './types'
import { RootState } from '@/types'
import { API, handleError } from '@/helpers/axios'
import i18next from 'i18next'
import i18nextConfig from '@/helpers/i18next-config'

i18next.init(i18nextConfig)

interface TownSettings {
  auto_mod_new_members: number;
  default_lang: string | null;
  description: string | null;
  emergency_moderation: string | null;
  members_require_approval: number;
}

export const actions: ActionTree<ModuleState, RootState> = {
  async addAutoNotice(_, { eventId, townId, noticeId }: { eventId: number; townId: number; noticeId: number }) {
    try {
      const response = await API.post(`api/group/${townId}/auto-notice/create`, { eventId, noticeId })
      return response.status === 200 ? response.data : response
    }
    catch(error) { handleError(error) }
  },
  async deleteAutoNotice(_, { eventId, noticeId, townId }: { eventId: number; noticeId: number; townId: number }) {
    try {
      const response = await API.delete(`api/group/${townId}/auto-notice/${noticeId}`, { data: { eventId } })
      if (response.status === 200) {
        eventHub.$emit('triggerSnackbar', {
          color: 'warning',
          text: i18next.t(`Removed notice.`),
        })
      }
      return response.data
    }
    catch(error) { handleError(error) }
  },
  async fetchAutoNotices(_, townId: number) {
    try {
      const response = await API.get(`api/group/${townId}/auto-notices`)
      return response.status === 200 ? response.data : response
    }
    catch(error) { handleError(error) }
  },
  async createNotice(_, { townId, payload }: {townId: number; payload: Notice}) {
    try {
      const response = await API.post(`api/group/${townId}/notice/create`, payload)
      return response.status === 200 ? response.data : response
    }
    catch(error) { handleError(error) }
  },
  async deleteNotice(_, { noticeId, townId }: { noticeId: number; townId: number }) {
    try {
      const response = await API.get(`api/group/${townId}/notice/${noticeId}/delete`)
      return response.status === 200 ? response.data : response
    }
    catch(error) { handleError(error) }
  },
  async fetchTowns({ commit }) {
    try {
      const response = await API.get('api/groups-by-moderator')
      if (response.status === 200) commit('handleFetchTowns', response.data)
      return
    }
    catch(error) { handleError(error) }
  },
  async fetchTownActivity(_, { itemsPerPage, page, townId }: {itemsPerPage: number; page: number; townId: number}) {
    try {
      const response = await API.get(`api/group/${townId}/activity`, { params: { itemsPerPage, page } })
      return response.status === 200 ? response.data : response
    }
    catch(error) { handleError(error) }
  },
  async fetchHotwords({ commit }, townId: number) {
    try {
      const response = await API.get(`api/group/${townId}/hotwords`)
      if(response.status === 200) {
        commit('handleFetchHotwords', { hotwords: response.data.hotwords_groups, townId })
        return response.data.hotwords_groups
      } else console.log('hotwords got only "response"', response)
    }
    catch (error) { handleError(error) }
  },
  async fetchTownInfo(_, townId: number) {
    try {
      const response = await API.get(`api/group/${townId}/info`)
      return response.status === 200 ? response.data : response
    }
    catch(error) { handleError(error) }
  },
  async fetchTownSettings(_, townId: number) {
    try {
      const response = await API.get(`api/group/${townId}/settings`)
      return response.status === 200 ? response.data : response
    }
    catch(error) { handleError(error) }
  },
  async resetTownSettings(_, townId: number) {
    try {
      const response = await API.post(`api/group/${townId}/settings/reset`)
      return response.status === 200 ? response.data : response
    }
    catch (error) { handleError(error) }
  },
  async bulkMailNotice(_, {noticeId, townId}: {noticeId: number; townId: number}) {
    try {
        const response = await API.get(`api/group/${townId}/notice/${noticeId}/bulk-send`)
        if (response.status > 299) {
          throw response
        }

        return response.data
    }
    catch(error) {
      handleError({ response: error, config: error.config})
      throw error;
    }
  },
  async fetchNotice(_, {noticeId, townId}: {noticeId: number; townId: number}) {
    try {
      const response = await API.get(`api/group/${townId}/notice/${noticeId}`)
      return response.status === 200 ? response.data : response
    }
    catch(error) { handleError(error) }
  },
  async fetchNotices({ commit }, townId: number) {
    try {
      const response = await API.get(`api/group/${townId}/notices`)
      if (response.status === 200) {
        let notices = response.data.notices.map((notice: {id: number; description: string}) => {
          return {value: notice.id, text: notice.description}
        })
        notices = [{value: 0, text: 'Custom rejection message'}].concat(notices)
        commit('handleFetchNotices', { notices, townId })
        return notices
      }
    }
    catch(error) { handleError(error) }
  },
  async fetchNoticesFull({ commit }, townId: number) {
    try {
      const response = await API.get(`api/group/${townId}/notices-full`)
      if (response.status === 200) {
        const notices = response.data.notices
        commit('handleFetchNotices', { notices, townId })
        return notices
      }
    }
    catch(error) { handleError(error) }
  },
  async updateNoticesRanking(_, {noticesRanking, townId} : {noticesRanking: [number, number][], townId: number}) {
    try {
      const response = await API.post(`api/group/${townId}/update-notices-ranking`, {noticesRanking})
      return response.status === 200 ? response.data : response
    }
    catch(error) { handleError(error) }
  },
  async updateNotice(_, { townId, payload }: {townId: number; payload: Notice}) {
    try {
      const response = await API.post(`api/group/${townId}/notice/${payload.id}/edit`, payload)
      return response.status === 200 ? response.data : response
    }
    catch(error) { handleError(error) }
  },
  async updateTown(_, { townId, payload }: {townId: number; payload: TownSettings}) {
    try {
      const response = await API.post(`api/group/${townId}/update`, payload)
      return response.status === 200 ? response.data : response
    }
    catch(error) { handleError(error) }
  },
  async createHotwords(_, { hotwords, townId }: { hotwords: string[]; townId: number }) {
    try {
      const response = await API.post(`api/group/${townId}/hotwords`, { hotwords, group_id: townId })
      return response.status === 200 ? response.data : response
    }
    catch (error) { handleError(error) }
  },
  async deleteHotwords(_, { hotwords, townId }: { hotwords: number[]; townId: number }) {
    const response = await API.delete(`api/group/${townId}/hotwords/${hotwords.join(',')}`)
    return response.status === 200 ? response.data : response
  },
  async bulkUnmoderateAll(_, townId: number) {
    try {
      const response = await API.get(`api/group/${townId}/bulk-unmoderate-all`)
      return response.status === 200 ? response.data : response
    }
    catch(error) { handleError(error) }
  }
}
